/*
 * MISS BULLITT — scroll-driven riders
 * Theme asset: assets/mb-riders.js
 *
 * Three Bullitts ride down the page as you scroll, each leaving a light trail,
 * racing each other, and finishing on a broadside Akira slide.
 * The bike is the real 33x12 sprite off the frame decal, recoloured per rider.
 *
 * FOUR IDEAS WORTH KNOWING
 *
 * 1. DOCUMENT SPACE. The canvas is fixed to the viewport, but every route is
 *    defined in document coordinates and only the on-screen slice is drawn.
 *    That is what makes one trail run the whole page instead of restarting per
 *    section — and why this works with sections it has never heard of.
 *
 * 2. FLYWHEEL. Scroll position sets a TARGET; each rider eases toward it with
 *    its own inertia. Flick the page and they spin up over about a second
 *    rather than teleporting. This is also the speed limit: no matter how hard
 *    you throw the scrollbar, a rider can only close the gap so fast, so it
 *    never turns into a blur. Every rider moves at one speed along its route —
 *    there is no separate weighting for turns.
 *
 * 3. RACING. Each rider carries a slow sinusoidal pace offset on its own phase,
 *    so the lead changes hands several times down the page instead of the first
 *    lane simply always being first.
 *
 * 4. START AND END ARE ANCHORED TO ELEMENTS, not to the document. The ride
 *    begins below the hero and finishes inside the outro, both found by CSS
 *    selector and re-measured on every layout change. Anchoring the end to the
 *    document height meant finishing inside the theme footer, because the
 *    footer is part of that height. Add sections between the two anchors and
 *    the ride simply gets longer.
 */
(function () {
  'use strict';

  var CELL = 4;

  function attr(el, n, d) { var v = el.getAttribute('data-' + n); return (v === null || v === '') ? d : v; }
  function num(el, n, d) { var v = parseFloat(attr(el, n, NaN)); return isNaN(v) ? d : v; }
  function bool(el, n, d) { var v = attr(el, n, null); return v === null ? d : (v === 'true' || v === '1'); }

  function hexToRgb(h) {
    h = String(h).replace('#', '').trim();
    if (h.length === 3) h = h[0] + h[0] + h[1] + h[1] + h[2] + h[2];
    var n = parseInt(h, 16);
    return isNaN(n) ? [255, 255, 255] : [(n >> 16) & 255, (n >> 8) & 255, n & 255];
  }
  function mix(hex, target, amount) {
    var a = hexToRgb(hex), b = hexToRgb(target);
    return 'rgb(' + Math.round(a[0] + (b[0] - a[0]) * amount) + ',' +
                    Math.round(a[1] + (b[1] - a[1]) * amount) + ',' +
                    Math.round(a[2] + (b[2] - a[2]) * amount) + ')';
  }
  function mulberry32(a) {
    return function () {
      a |= 0; a = (a + 0x6D2B79F5) | 0;
      var t = Math.imul(a ^ (a >>> 15), 1 | a);
      t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
      return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
    };
  }
  function clamp01(v) { return v < 0 ? 0 : (v > 1 ? 1 : v); }

  var reduceMotion = window.matchMedia &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function Riders(root) {
    this.root = root;
    this.canvas = root.querySelector('[data-mb-canvas]');
    if (!this.canvas) return;
    this.ctx = this.canvas.getContext('2d');

    this.cfg = {
      colorA: attr(root, 'color-a', '#582B94'),
      colorB: attr(root, 'color-b', '#F2D5ED'),
      colorC: attr(root, 'color-c', '#D83422'),
      lanes: Math.max(1, Math.min(3, num(root, 'lanes', 3))),
      mobileLanes: Math.max(1, Math.min(3, num(root, 'mobile-lanes', 1))),
      laneGap: num(root, 'lane-gap', 14),
      laneCentre: num(root, 'lane-centre', 50),
      thickness: Math.max(1, num(root, 'thickness', 2)),
      headFrac: num(root, 'head', 0.58),
      stagger: num(root, 'stagger', 40),
      trailFade: num(root, 'trail-fade', 0),
      mobileMax: num(root, 'mobile-max', 749),
      showHeads: bool(root, 'heads', true),
      bikeScale: Math.max(1, num(root, 'bike-scale', 2)),
      startPad: num(root, 'start-pad', 0),
      endOffset: num(root, 'end-offset', 80),
      startAfter: attr(root, 'start-after', ''),
      endAt: attr(root, 'end-at', ''),
      seed: num(root, 'seed', 7),
      runMin: num(root, 'run-min', 90),
      runMax: num(root, 'run-max', 340),
      jogMin: num(root, 'jog-min', 70),
      jogMax: num(root, 'jog-max', 150),
      opacity: num(root, 'opacity', 100) / 100,
      // flywheel
      flywheel: Math.max(0, num(root, 'flywheel', 760)),   // ms to spin up
      // racing
      raceAmount: num(root, 'race-amount', 4) / 100,       // as a share of the page
      raceCycles: num(root, 'race-cycles', 3),
      // the slide
      slide: bool(root, 'slide', true),
      slideLen: num(root, 'slide-len', 260),
      slideTurn: num(root, 'slide-turn', 90),
      finishAt: num(root, 'finish-at', 88) / 100,
      /* 0, matching the section's own default. It used to be 320 because the
         slide was positioned from the BOTTOM OF THE PAGE and needed room below
         the last section. It is now anchored to the top of `endAt` (.mb-outro)
         and shortened to fit inside it, so that run-off is dead space — it
         renders as an empty band of the pink body background under the outro,
         after the halftone has stopped. Only raise this if you clear the
         finish selector and let the ride run to the bottom of the page. */
      finishSpace: num(root, 'finish-space', 0),
      skidGap: Math.max(2, num(root, 'skid-gap', 3))
    };

    this.dpr = 1; this.raf = 0; this.lastT = 0;
    this.state = [];                     // per-rider eased progress
    var self = this;
    this.onScroll = function () { self.awake = true; };
    this.onResize = function () { self.measure(); self.awake = true; };

    this.measure();
    window.addEventListener('scroll', this.onScroll, { passive: true });
    window.addEventListener('resize', this.onResize);

    /* The page grows as images load and as you add sections. Re-derive rather
       than trusting anything measured at boot. */
    if ('ResizeObserver' in window) {
      this.ro = new ResizeObserver(function () {
        if (Math.abs(self.docHeight() - self.pageH) > 8) { self.measure(); self.awake = true; }
      });
      this.ro.observe(document.documentElement);
      if (document.body) this.ro.observe(document.body);
    }
    this.loop();
  }

  Riders.prototype.docHeight = function () {
    var d = document.documentElement, b = document.body;
    return Math.max(b.scrollHeight, d.scrollHeight, b.offsetHeight, d.offsetHeight, d.clientHeight);
  };

  Riders.prototype.measure = function () {
    var vw = window.innerWidth, vh = window.innerHeight;
    this.vw = vw; this.vh = vh;
    this.dpr = Math.min(window.devicePixelRatio || 1, 2);

    this.canvas.width = Math.round(vw * this.dpr);
    this.canvas.height = Math.round(vh * this.dpr);
    this.ctx.setTransform(this.dpr, 0, 0, this.dpr, 0, 0);
    this.ctx.imageSmoothingEnabled = false;

    this.mobile = vw <= this.cfg.mobileMax;
    this.laneCount = this.mobile ? this.cfg.mobileLanes : this.cfg.lanes;
    this.pageH = this.docHeight();

    /* Start below a named element — the hero by default, so the bikes ride out
       from under the header image rather than sitting on it before you scroll. */
    var startY = this.cfg.startPad;
    if (this.cfg.startAfter) {
      var anchor = document.querySelector(this.cfg.startAfter);
      if (anchor) {
        var r = anchor.getBoundingClientRect();
        var sy = window.pageYOffset || document.documentElement.scrollTop || 0;
        startY = Math.max(startY, r.bottom + sy);
      }
    }
    this.startY = startY;
    this.ensureFinishSpace();
    this.pageH = this.docHeight();        // the spacer may have just grown it

    /* The anchor marks where the SLIDE BEGINS — the top of the outro, plus an
       offset — not where the ride ends.

       Anchoring the ride's END to the element meant the slide, being slideLen
       tall, necessarily STARTED slideLen above it, i.e. up in the previous
       section. Marking the start of the slide and deriving the end from it is
       what keeps the whole manoeuvre inside one section. */
    var slideTop = null;
    // Effective slide length: shrinks if the target section cannot hold a full one.
    this.slideLen = this.cfg.slide ? this.cfg.slideLen : 0;
    if (this.cfg.endAt) {
      var target = document.querySelector(this.cfg.endAt);
      if (target) {
        var tr = target.getBoundingClientRect();
        var sy2 = window.pageYOffset || document.documentElement.scrollTop || 0;
        var top = tr.top + sy2;
        /* Keep the whole slide inside the element. Two ways it can escape:
           the offset is set deeper than the section can hold, or the section is
           simply shorter than the slide. Clamping the start fixes the first;
           only shortening the slide fixes the second. */
        if (this.cfg.slide) {
          this.slideLen = Math.max(CELL * 8, Math.min(this.cfg.slideLen, tr.height - 8));
        }
        slideTop = top + this.cfg.endOffset;
        var latest = top + tr.height - this.slideLen;
        if (slideTop > latest) slideTop = Math.max(top, latest);
      }
    }
    if (slideTop === null) {
      slideTop = this.pageH - this.cfg.endOffset - this.slideLen;
    }
    this.slideTop = Math.max(startY + 300, slideTop);
    this.endY = this.slideTop + this.slideLen;

    this.buildPaths();
  };

  /* Empty run-off at the end so the slide is not laid over the closing copy.
     Inserted after the LAST of our own sections rather than at the end of the
     document, so it lands above the theme footer — and it re-finds that spot
     whenever sections are added or reordered. */
  Riders.prototype.ensureFinishSpace = function () {
    var h = this.cfg.finishSpace;
    var el = document.querySelector('[data-mb-finish-space]');
    if (h <= 0) { if (el && el.parentNode) el.parentNode.removeChild(el); return; }

    var ours = document.querySelectorAll('.mb-bleed, .mb-story, .mb-outro');
    var last = ours.length ? ours[ours.length - 1] : null;
    if (!last) { if (el && el.parentNode) el.parentNode.removeChild(el); return; }
    // Do not measure the spacer as if it were content.
    if (last.hasAttribute && last.hasAttribute('data-mb-finish-space')) {
      last = ours.length > 1 ? ours[ours.length - 2] : null;
      if (!last) return;
    }
    var anchor = last.closest ? (last.closest('.shopify-section') || last) : last;

    if (!el) {
      el = document.createElement('div');
      el.setAttribute('data-mb-finish-space', '');
      el.setAttribute('aria-hidden', 'true');
      el.style.cssText = 'width:100%;pointer-events:none;';
    }
    el.style.height = h + 'px';
    if (el.previousElementSibling !== anchor || !el.parentNode) {
      anchor.parentNode.insertBefore(el, anchor.nextSibling);
    }
  };

  /* ------------------------------------------------------------ pathing ---
     Orthogonal route inside a per-lane corridor: run down, hard turn, run
     across, hard turn. Finishes with a long broadside slide. */
  Riders.prototype.buildPaths = function () {
    var trailW = this.cfg.thickness * CELL;
    /* The lane gap must fit a corridor PLUS the trail's own width, twice over —
       a corridor of width C actually occupies C + trailW because the trail is
       drawn from its x rightwards. */
    var gapPx = Math.max(this.vw * (this.cfg.laneGap / 100), 2 * trailW + 12);
    var centre = this.vw * (this.cfg.laneCentre / 100);
    var spread = (this.laneCount - 1) / 2;
    var corridor = Math.max(trailW + 4, gapPx - trailW - 6);

    /* A lone rider has no neighbour to avoid, so it should not be boxed into a
       corridor sized for one. On a phone that came out around 12px — no room
       for any turn at all — and the single rider just ran straight down. */
    if (this.laneCount === 1) {
      corridor = Math.max(corridor, Math.min(this.vw * 0.68, this.vw - trailW - 32));
    }

    this.paths = [];
    for (var lane = 0; lane < this.laneCount; lane++) {
      var base = centre + (lane - spread) * gapPx;
      var lo = base - corridor / 2, hi = base + corridor / 2;
      var rnd = mulberry32((this.cfg.seed | 0) + lane * 9176);

      // A turn has to be worth making, and has to fit the corridor.
      var minJog = Math.min(this.cfg.jogMin, corridor * 0.38);
      var maxJog = Math.max(minJog, Math.min(this.cfg.jogMax, corridor * 0.92));

      // Ordinary riding stops exactly where the slide begins.
      var rideEnd = Math.max(this.startY + 200, this.slideTop);

      var pts = [], x = Math.round(base / CELL) * CELL, y = this.startY;
      pts.push([x, y]);
      var guard = 0;
      while (y < rideEnd && guard++ < 4000) {
        var run = this.cfg.runMin + rnd() * (this.cfg.runMax - this.cfg.runMin);
        y = Math.min(rideEnd, y + run);
        pts.push([x, y]);
        if (y >= rideEnd) break;

        var dir = rnd() < 0.5 ? -1 : 1;
        var mag = minJog + rnd() * (maxJog - minJog);
        var nx = x + dir * mag;
        if (nx < lo || nx > hi) nx = x - dir * mag;         // turn the other way
        nx = Math.max(lo, Math.min(hi, nx));
        if (Math.abs(nx - x) < minJog) continue;            // no room: keep going down
        x = Math.round(nx / CELL) * CELL;
        pts.push([x, y]);
      }

      /* THE SLIDE.
         One last hard turn to lay the bike over, then it keeps going DOWN the
         page while pointing sideways — an Akira slide. The earlier version slid
         horizontally with the bike facing down, which read as the bike moving
         one way while aimed another. Travel stays downward; only the bike's
         heading turns. */
      var slideAt = -1, slideFace = 'right';
      if (this.cfg.slide && this.slideLen > 0) {
        var toCentre = centre - x;
        var sdir = toCentre === 0 ? (lane % 2 ? 1 : -1) : (toCentre > 0 ? 1 : -1);
        var turn = Math.min(this.cfg.slideTurn, corridor * 0.9);
        var tx = x + sdir * turn;
        tx = Math.max(trailW + 8, Math.min(this.vw - trailW - 8, tx));
        tx = Math.round(tx / CELL) * CELL;
        if (tx !== x) pts.push([tx, y]);            // the hard turn
        slideFace = sdir > 0 ? 'right' : 'left';
        slideAt = pts.length - 1;                    // the slide is what follows
        pts.push([tx, Math.round(this.endY)]);       // broadside, still heading down
      }

      // Uniform arc length: one speed everywhere, straights and turns alike.
      var acc = [0], total = 0;
      for (var i = 1; i < pts.length; i++) {
        total += Math.abs(pts[i][0] - pts[i - 1][0]) + Math.abs(pts[i][1] - pts[i - 1][1]);
        acc.push(total);
      }
      this.paths.push({
        pts: pts, acc: acc, len: total,
        slideAt: slideAt, slideFace: slideFace,
        slideDist: slideAt >= 0 ? acc[slideAt] : Infinity
      });

      if (!this.state[lane]) this.state[lane] = { cur: 0 };
    }
  };

  Riders.prototype.at = function (p, dist) {
    if (dist <= 0) return { x: p.pts[0][0], y: p.pts[0][1], dir: 'down', sliding: false };
    var n = p.pts.length;
    if (dist >= p.len) {
      var a = p.pts[n - 2] || p.pts[0], b = p.pts[n - 1];
      return {
        x: b[0], y: b[1],
        dir: b[1] !== a[1] ? 'down' : (b[0] > a[0] ? 'right' : 'left'),
        sliding: p.slideAt >= 0, face: p.slideFace
      };
    }
    var lo = 0, hi = n - 1;
    while (lo < hi - 1) { var mid = (lo + hi) >> 1; if (p.acc[mid] <= dist) lo = mid; else hi = mid; }
    var s = p.pts[lo], e = p.pts[lo + 1];
    var t = (dist - p.acc[lo]) / Math.max(1, p.acc[lo + 1] - p.acc[lo]);
    return {
      x: s[0] + (e[0] - s[0]) * t,
      y: s[1] + (e[1] - s[1]) * t,
      dir: e[1] !== s[1] ? 'down' : (e[0] > s[0] ? 'right' : 'left'),
      sliding: lo >= p.slideAt && p.slideAt >= 0, face: p.slideFace
    };
  };

  /* ------------------------------------------------------------- sprite --- */
  Riders.prototype.drawBike = function (cx, cy, dir, color, sliding, slideFace) {
    var B = window.MB_BIKE;
    var ctx = this.ctx, s = this.cfg.bikeScale;
    if (!B) {
      ctx.fillStyle = mix(color, '#ffffff', 0.5);
      ctx.fillRect(Math.round(cx) - CELL, Math.round(cy) - CELL, CELL * 3, CELL * 3);
      return;
    }
    // Broadside through the slide: the bike points left or right while it keeps
    // travelling down the page. That is the slide; facing down would just be
    // riding normally.
    var face = sliding ? (slideFace || 'right') : dir;
    var tones = [mix(color, '#291E41', 0.55), color, mix(color, '#ffffff', 0.6)];
    var w = B.w, h = B.h;
    for (var ry = 0; ry < h; ry++) {
      var row = B.rows[ry];
      for (var rx = 0; rx < w; rx++) {
        var ch = row.charCodeAt(rx) - 48;
        if (ch < 0 || ch > 2) continue;
        var px, py;
        if (face === 'down')       { px = (h - 1 - ry); py = rx; }
        else if (face === 'right') { px = rx;           py = ry; }
        else                       { px = (w - 1 - rx); py = (h - 1 - ry); }
        var ox = face === 'down' ? h : w, oy = face === 'down' ? w : h;
        ctx.fillStyle = tones[ch];
        ctx.fillRect(
          Math.round(cx + (px - ox / 2) * s),
          Math.round(cy + (py - oy / 2) * s),
          s, s
        );
      }
    }
  };

  /* Skid: two dashed streaks instead of a solid trail. The slide runs down the
     page, so the streaks are vertical and sit either side of the wheel line. */
  Riders.prototype.drawSkidV = function (x, y0, y1, w, color) {
    var ctx = this.ctx;
    var step = CELL * this.cfg.skidGap;
    var from = Math.min(y0, y1), to = Math.max(y0, y1);
    ctx.fillStyle = mix(color, '#291E41', 0.5);
    for (var y = from; y <= to; y += step) {
      ctx.fillRect(Math.round(x), Math.round(y), w, CELL * 2);
      ctx.fillRect(Math.round(x + w + CELL), Math.round(y), w, CELL * 2);
    }
  };

  Riders.prototype.paint = function (dt) {
    var ctx = this.ctx;
    if (!ctx || !this.paths) return false;
    ctx.clearRect(0, 0, this.vw, this.vh);
    ctx.globalAlpha = this.cfg.opacity;

    var scrollY = window.pageYOffset || document.documentElement.scrollTop || 0;
    var cols = [this.cfg.colorA, this.cfg.colorB, this.cfg.colorC];
    var w = this.cfg.thickness * CELL;
    var span = Math.max(1, this.endY - this.startY);
    var moving = false;

    for (var lane = 0; lane < this.paths.length; lane++) {
      var p = this.paths[lane];
      if (!p.len) continue;
      var col = cols[lane % cols.length];
      var st = this.state[lane] || (this.state[lane] = { cur: 0 });

      /* Raw progress from the scroll, before any per-rider offsets. */
      var headY = scrollY + this.vh * this.cfg.headFrac;
      var raw = clamp01((headY - this.startY) / span);

      /* THE FINISH LINE.
         Past finishAt, every per-rider offset fades out and the target is pulled
         to exactly 1, so all three complete the slide.

         This is the bug that left two of them stranded: both the stagger and the
         racing surge SUBTRACT from progress, and at the page bottom raw is
         already 1 — so clamp01 capped whoever was surging forward but left the
         others permanently short of the end. Only the middle rider happened to
         land on the finish. Fading the offsets to zero is what makes the finish
         a place all three actually arrive at. */
      var fin = this.cfg.finishAt >= 1 ? 0
        : clamp01((raw - this.cfg.finishAt) / (1 - this.cfg.finishAt));
      var mod = 1 - fin;

      var target = raw - (lane * this.cfg.stagger / span) * mod;
      if (this.cfg.raceAmount > 0) {
        var phase = lane * 2.399;                     // irrational-ish, avoids sync
        target += Math.sin(raw * Math.PI * 2 * this.cfg.raceCycles + phase) *
                  this.cfg.raceAmount * mod;
      }
      target = clamp01(target);
      target = target + (1 - target) * fin;           // everyone lands on the line

      /* Flywheel: ease toward the target with per-rider inertia. This is also
         the speed limiter — however hard the page is flung, a rider closes only
         a fraction of the gap per frame, so it can never streak across. */
      if (reduceMotion || this.cfg.flywheel <= 0) {
        st.cur = target;
      } else {
        var tau = this.cfg.flywheel * (1 + lane * 0.18);
        var k = 1 - Math.exp(-dt / tau);
        st.cur += (target - st.cur) * k;
        if (Math.abs(target - st.cur) > 0.0004) moving = true;
      }

      var headDist = st.cur * p.len;

      // Trail up to the head, clipped to the viewport.
      for (var i = 1; i < p.pts.length; i++) {
        if (p.acc[i - 1] >= headDist) break;
        var a = p.pts[i - 1], b = p.pts[i];
        var t = Math.min(1, (headDist - p.acc[i - 1]) / Math.max(1, p.acc[i] - p.acc[i - 1]));
        var bx = a[0] + (b[0] - a[0]) * t, by = a[1] + (b[1] - a[1]) * t;

        var y0 = Math.min(a[1], by), y1 = Math.max(a[1], by);
        if (y1 < scrollY - CELL || y0 > scrollY + this.vh + CELL) continue;

        if (this.cfg.trailFade > 0) {
          var age = (headDist - p.acc[i - 1]) / Math.max(1, p.len);
          ctx.globalAlpha = this.cfg.opacity * Math.max(0.18, 1 - age * this.cfg.trailFade);
        }

        if (p.slideAt >= 0 && i - 1 >= p.slideAt) {
          this.drawSkidV(a[0], a[1] - scrollY, by - scrollY, w, col);
        } else if (a[1] === b[1]) {
          var xa = Math.min(a[0], bx), xb = Math.max(a[0], bx);
          ctx.fillStyle = col;
          ctx.fillRect(Math.round(xa), Math.round(a[1] - scrollY), Math.round(xb - xa) + w, w);
        } else {
          ctx.fillStyle = col;
          ctx.fillRect(Math.round(a[0]), Math.round(y0 - scrollY), w, Math.round(y1 - y0) + w);
        }
      }
      ctx.globalAlpha = this.cfg.opacity;

      if (this.cfg.showHeads) {
        var hd = this.at(p, headDist);
        var vy = hd.y - scrollY;
        if (vy > -80 && vy < this.vh + 80) {
          this.drawBike(hd.x + w / 2, vy + w / 2, hd.dir, col, hd.sliding, hd.face);
        }
      }
    }
    ctx.globalAlpha = 1;
    return moving;
  };

  Riders.prototype.loop = function () {
    var self = this;
    this.awake = true;
    var tick = function (ts) {
      self.raf = requestAnimationFrame(tick);
      var dt = self.lastT ? Math.min(64, ts - self.lastT) : 16;
      self.lastT = ts;
      if (!self.awake) return;
      // Keep painting while the flywheel is still catching up.
      var moving = self.paint(dt);
      if (!moving) self.awake = false;
    };
    this.raf = requestAnimationFrame(tick);
  };

  Riders.prototype.destroy = function () {
    cancelAnimationFrame(this.raf);
    window.removeEventListener('scroll', this.onScroll);
    window.removeEventListener('resize', this.onResize);
    if (this.ro) this.ro.disconnect();
  };

  /* --------------------------------------------------- collage reveals --- */
  function watchCollage(scope) {
    var items = (scope || document).querySelectorAll('.mb-collage__item');
    if (!items.length) return;
    if (!('IntersectionObserver' in window) || reduceMotion) {
      for (var i = 0; i < items.length; i++) items[i].classList.add('is-in');
      return;
    }
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        if (!e.isIntersecting) return;
        var el = e.target, d = parseInt(el.getAttribute('data-delay') || '0', 10);
        setTimeout(function () { el.classList.add('is-in'); }, d);
        io.unobserve(el);
      });
    }, { rootMargin: '0px 0px -12% 0px', threshold: 0.05 });
    for (var k = 0; k < items.length; k++) io.observe(items[k]);
  }

  function remeasureAll() {
    document.querySelectorAll('[data-mb-riders]').forEach(function (el) {
      if (el.__mbRiders) { el.__mbRiders.measure(); el.__mbRiders.awake = true; }
    });
  }

  function initAll(scope) {
    (scope || document).querySelectorAll('[data-mb-riders]').forEach(function (el) {
      if (el.__mbRiders) return;
      el.__mbRiders = new Riders(el);
    });
    watchCollage(scope);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initAll(); });
  } else { initAll(); }

  window.addEventListener('load', remeasureAll);

  document.addEventListener('shopify:section:load', function (e) {
    var old = e.target.querySelector('[data-mb-riders]');
    if (old && old.__mbRiders) { old.__mbRiders.destroy(); old.__mbRiders = null; }
    initAll(e.target);
    remeasureAll();                       // the page just got taller or shorter
  });
  document.addEventListener('shopify:section:unload', function (e) {
    var el = e.target.querySelector('[data-mb-riders]');
    if (el && el.__mbRiders) { el.__mbRiders.destroy(); el.__mbRiders = null; }
    setTimeout(remeasureAll, 0);
  });

  window.MBRiders = Riders;
})();
